import { useId, useState, type ReactNode } from 'react';
import { ArrowUpRight, ChevronDown, Play } from 'lucide-react';
import {
  ACTION_STATUS_LABELS,
  type V2ActionStatus,
  type V2ConnectItem,
  type V2LearnItem,
  type V2NeedSection,
  type V2ReportNeed,
  type V2SupportItem,
} from '../api/v2ReportApi';
import { SafeLink } from './SupportItems';

/** Offered in the order a client moves through them, with Later last. */
const ACTION_STATUSES: V2ActionStatus[] = ['NOT_STARTED', 'IN_PROGRESS', 'COMPLETE', 'DEFERRED'];

const LABELS: Record<V2NeedSection, string> = {
  connect: 'Connect for help',
  advisor: 'Ask your advisor',
  learn: 'Learn and discover',
};

const EYEBROW = 'text-[11px] font-semibold uppercase tracking-wider text-[#026857]';
const BUTTON =
  'shrink-0 rounded-full bg-[#026857] px-3.5 py-1.5 text-xs font-semibold text-white hover:bg-[#04332b]';

const count = (n: number, one: string, many: string) => `${n} ${n === 1 ? one : many}`;

/** Inside a fold the frame already draws the box: rows split by a hairline, lined up with its label from sm up. */
const FRAMED_LIST = 'divide-y divide-gray-100';
const FRAMED_ROW = 'px-4 sm:pl-11';

function Workbook({ item }: { item: V2SupportItem }) {
  return (
    <div className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-[#d8e5e1] bg-white px-4 py-2.5">
      <span className="min-w-0 text-[14px] font-medium text-gray-900">{item.label}</span>
      <SafeLink href={item.href} label={`Open ${item.label}`} className={BUTTON}>
        Open
      </SafeLink>
    </div>
  );
}

function ConnectList({ items, framed }: { items: V2ConnectItem[]; framed: boolean }) {
  return (
    <ul className={framed ? FRAMED_LIST : 'space-y-2'}>
      {items.map((item) => (
        <li
          key={item.label}
          className={`flex flex-wrap items-center gap-3 py-3 ${framed ? FRAMED_ROW : 'rounded-xl border border-gray-200 px-4'}`}
        >
          <span className="min-w-0 flex-1 text-[15px] font-medium text-gray-900">
            <span className="mr-2 inline-block rounded-full bg-[#f3efe6] px-2 py-0.5 align-middle text-[11px] font-semibold uppercase tracking-wide text-gray-600">
              {item.role === 'primary' ? 'Primary' : 'Secondary'}
            </span>
            {item.label}
            {item.note && <span className="font-normal text-gray-500"> {item.note}</span>}
          </span>
          {item.explainers.map((href, i) => (
            <SafeLink
              key={href}
              href={href}
              label={`Explainer video${item.explainers.length > 1 ? ` ${i + 1}` : ''}: ${item.label}`}
              className="inline-flex items-center gap-1.5 text-[13px] font-medium text-[#026857] hover:underline"
            >
              <Play className="h-3.5 w-3.5" aria-hidden /> Explainer video
            </SafeLink>
          ))}
          <SafeLink href={item.href} label={`Connect with ${item.label}`} className={BUTTON}>
            Connect
          </SafeLink>
        </li>
      ))}
    </ul>
  );
}

function LearnList({ items, framed }: { items: V2LearnItem[]; framed: boolean }) {
  const row = `flex items-center justify-between gap-3 py-2.5 text-[14px] ${framed ? FRAMED_ROW : 'rounded-lg border border-gray-200 px-4'}`;
  return (
    <ul className={framed ? FRAMED_LIST : 'space-y-1.5'}>
      {items.map((item, i) => {
        const text = (
          <span className="min-w-0">
            {item.kind && <span className="mr-2 text-xs text-gray-500">{item.kind}</span>}
            {item.label}
          </span>
        );
        return (
          <li key={`${item.label}-${i}`}>
            <SafeLink
              href={item.href}
              className={`${row} font-medium text-[#026857] hover:border-[#026857] hover:bg-[#eaf1ee]`}
              fallback={<div className={`${row} text-gray-700`}>{text}</div>}
            >
              {text}
              <ArrowUpRight className="h-4 w-4 shrink-0" aria-hidden />
            </SafeLink>
          </li>
        );
      })}
    </ul>
  );
}

/** A later section, folded behind its name and how much it holds. */
function Folded({ label, hint, children }: { label: string; hint: string; children: ReactNode }) {
  const [open, setOpen] = useState(false);
  const panelId = useId();
  return (
    <div className="overflow-hidden rounded-xl border border-gray-200">
      <button
        type="button"
        aria-expanded={open}
        aria-controls={open ? panelId : undefined}
        onClick={() => setOpen((value) => !value)}
        className="flex w-full items-center gap-3 px-4 py-2.5 text-left hover:bg-[#fbf9f5]"
      >
        <ChevronDown
          className={`h-4 w-4 shrink-0 text-gray-500 transition-transform motion-reduce:transition-none ${open ? 'rotate-180' : ''}`}
          aria-hidden
        />
        <span className="flex flex-col">
          <span className="text-[14px] font-medium text-gray-900">{label}</span>
          <span className="text-[13px] text-gray-500">{hint}</span>
        </span>
      </button>
      {open && (
        <div id={panelId} className="border-t border-gray-100">
          {children}
        </div>
      )}
    </div>
  );
}

function Section({
  section,
  need,
  open,
}: {
  section: V2NeedSection;
  need: V2ReportNeed;
  open: boolean;
}) {
  const label = LABELS[section];
  if (section === 'advisor') {
    return (
      <div>
        <div className={EYEBROW}>{label}</div>
        {/* The matrix copy carries its own quotation marks. */}
        <p className="mt-1.5 text-[14px] leading-relaxed text-gray-800">{need.advisorQuestion}</p>
      </div>
    );
  }
  const body =
    section === 'connect' ? (
      <ConnectList items={need.connect} framed={!open} />
    ) : (
      <LearnList items={need.learn} framed={!open} />
    );
  if (!open) {
    const hint =
      section === 'connect'
        ? count(need.connect.length, 'recommended professional', 'recommended professionals')
        : count(need.learn.length, 'resource', 'resources');
    return (
      <Folded label={label} hint={hint}>
        {body}
      </Folded>
    );
  }
  return (
    <div>
      <div className={`${EYEBROW} mb-2`}>{label}</div>
      {body}
    </div>
  );
}

/**
 * One need's detail on the client report, in the top 3 or opened from a timing
 * group. Draws what the payload decided: which sections show and in what
 * order, the workbook (or what stands in for it) and whether the need takes a
 * status. The first section leads open; Ask your advisor is one line, so it is
 * never folded; anything after it starts folded.
 */
export function V2NeedDetail({
  need,
  onStatusChange,
  disabled = false,
}: {
  need: V2ReportNeed;
  /** Omitted for a viewer who may not change it (the advisor). */
  onStatusChange?: (needId: string, status: V2ActionStatus) => void;
  disabled?: boolean;
}) {
  return (
    <div className="space-y-4">
      <p className="text-[15px] leading-relaxed text-gray-800">{need.why}</p>

      <div className="space-y-2.5 rounded-xl bg-[#eaf1ee] p-4">
        <div className={EYEBROW}>Start here</div>
        <p className="text-[14px] leading-relaxed text-gray-800">{need.startHere}</p>
        {need.workbook && <Workbook item={need.workbook} />}
      </div>

      {need.sections.map((section, i) => (
        <Section key={section} section={section} need={need} open={i === 0} />
      ))}

      {need.status && onStatusChange && (
        <div className="border-t border-gray-100 pt-4">
          <div className="mb-2 text-[11px] font-semibold uppercase tracking-wider text-gray-500">
            Where are you with this?
          </div>
          <div className="flex flex-wrap gap-2" role="group" aria-label="Progress on this priority">
            {ACTION_STATUSES.map((status) => {
              const selected = need.status === status;
              return (
                <button
                  key={status}
                  type="button"
                  aria-pressed={selected}
                  disabled={disabled}
                  onClick={() => onStatusChange(need.needId, status)}
                  className={`rounded-full border px-3.5 py-1.5 text-xs font-medium transition-colors motion-reduce:transition-none ${
                    selected
                      ? 'border-[#026857] bg-[#026857] text-white'
                      : 'border-gray-200 bg-white text-gray-700 hover:border-[#026857] disabled:opacity-60'
                  }`}
                >
                  {ACTION_STATUS_LABELS[status]}
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
